const shader = `
#define PRECISION 0.0001
#define DISTANCE_FROM_PLANE 0.5

attribute float intensity;
attribute float classification;
attribute int gpu_index;
attribute vec3 rgb;

uniform bool gpuPicker;
uniform float pratio;
uniform float size;
uniform float alpha;
uniform float distance;
uniform vec3 origin;
uniform float screenHeight;
uniform float fov;
uniform float size_min;
uniform float size_max;
uniform float octree_size;
uniform vec3 octree_corner;
uniform float octree_spacing;

uniform sampler2D color_map;
uniform float color_type;
uniform bool dynamic_size;
uniform bool dynamic_cloud;
uniform vec2 height_range;
uniform vec2 height_clamp;
uniform vec2 intensity_range;
uniform vec2 intensity_clamp;
uniform float rgb_max;

uniform sampler2D visible_nodes;
uniform sampler2D classification_data;
uniform bool classification_visible[NUMBER_OF_CLASSIFICATIONS];

uniform vec3 area_measure_data[AREA_VERTICES_TOTAL];
uniform float volume_status[AREA_TRIANGLES_TOTAL];

varying vec4 vGPUColor;
varying vec3 vColor;
varying vec2 vUv;
varying float vAlpha;
varying vec3 vViewPosition;

float sign (vec3 p1, vec3 p2, vec3 p3) {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
}

bool compareFloat(float floatOne, float floatTwo) {
    return abs(floatOne - floatTwo) < PRECISION;
}

vec3 pointOnPlane(vec3 origin, vec3 point, vec3 normal) {
    return point - dot(point - origin, normal) * normal;
}

float distanceFromPlane(vec3 origin, vec3 point, vec3 normal) {
    return abs(dot(origin - point, normal));
}

int checkPointInTriangle(vec3 point) {
    // Return values:
    // 0: not in polygon
    // 1: in polygon, volume not enabled
    // 2: in polygon, volume enabled

    for (int i=0;i<AREA_TRIANGLES_TOTAL; i++) {
        int baseIndex = i * 3;

        float volumeStatus = volume_status[i];
        bool isVolume = volumeStatus > 0.5;
        bool volumeActive = compareFloat(volumeStatus, 2.0);

        vec3 v1 = area_measure_data[baseIndex];
        vec3 v2 = area_measure_data[baseIndex + 1];
        vec3 v3 = area_measure_data[baseIndex + 2];

        // Empty set of vertices
        if (length(v1 - v2) < PRECISION) {
            if (length(v3 - v1) < PRECISION) {
                break;
            }
        }

        vec3 N = cross(v2 - v1, v3 - v1);
        N = N / length(N);


        vec3 v1_p, v2_p, v3_p, point_p;

        if (isVolume == false) {
            // Check distance from plane
            float pointHeight = distanceFromPlane(v1, point, N);
            if (pointHeight > DISTANCE_FROM_PLANE) {
                continue;
            }

            // Project point to plane
            v1_p = pointOnPlane(v1, v1, N);
            v2_p = pointOnPlane(v1, v2, N);
            v3_p = pointOnPlane(v1, v3, N);
            point_p = pointOnPlane(v1, point, N);
        } else {
            // Dont reproject our points
            v1_p = v1;
            v2_p = v2;
            v3_p = v3;
            point_p = point;
        }

        float d1 = sign(point_p, v1_p, v2_p);
        float d2 = sign(point_p, v2_p, v3_p);
        float d3 = sign(point_p, v3_p, v1_p);
        bool isNegative = (d1 < 0.0) || (d2 < 0.0) || (d3 < 0.0);
        bool isPositive = (d1 > 0.0) || (d2 > 0.0) || (d3 > 0.0);
        bool insideTriangle = !(isNegative && isPositive);

        if (insideTriangle) {
            if (volumeActive) {
                return 2;
            } else {
                return 1;
            }
        }
    }

    return 0;
}

bool classificationIsHidden(float classification) {
    return classification_visible[int(classification)] == false;
}

vec3 classificationColor(float classification) {
    float x = (classification + 0.1) / float(NUMBER_OF_CLASSIFICATIONS);
    vec4 value = texture2D(classification_data, vec2(x, 0.5));
    return value.rgb;
}

float clampedValue(float data, vec2 data_range, vec2 data_clamp) {
    float scaled = (data - data_range.x) / (data_range.y - data_range.x);
    float clamped = (scaled - data_clamp.x) / (data_clamp.y - data_clamp.x);
    return clamped;
}

vec3 calculateOppositeColor(float color_type, vec3 color_value){
    vec3 result;
    vec3 opp = vec3(1.0, 1.0, 1.0);
    opp = opp - color_value;

    bool isIntensity = compareFloat(color_type, 0.0);
    bool isRGB = compareFloat(color_type, 1.0);
    bool isHeight = compareFloat(color_type, 2.0);
    bool isClassification = compareFloat(color_type, 3.0);

    if (isHeight) {
        result = opp;
    } else if (isIntensity) {
        result = vec3(1.0, 0.0, 0.0);
    } else if (isClassification) {
        result = vec3(1.0, 0.0, 0.0);
    } else if (isRGB) {
        result = vec3(1.0, 0.0, 0.0);
    } else {
        result = opp;
    }

    return result;
}

vec3 getCloudColor(float color_type, vec3 pos_world) {
    // Default color for invalid color types
    vec3 color = vec3(0.0, 0.0, 0.0);

    bool isIntensity = compareFloat(color_type, 0.0);
    bool isRGB = compareFloat(color_type, 1.0);
    bool isHeight = compareFloat(color_type, 2.0);
    bool isClassification = compareFloat(color_type, 3.0);

    if (isRGB) {
        // Color by RGB values
        color = rgb / rgb_max;
    } else if (isIntensity) {
        // Intensity colors with clamping
        float pc_intens = clampedValue(intensity, intensity_range, intensity_clamp);
        color = texture2D(color_map, vec2(pc_intens, 0.5)).rgb;
    } else if (isHeight) {
        // Height based coloring with clamping
        float height = pos_world.z;
        float pc_height = clampedValue(height, height_range, height_clamp);
        color = texture2D(color_map, vec2(pc_height, 0.5)).rgb;
    } else if (isClassification) {
        color = classificationColor(classification);
    }

    return color;
}

float round(float number){
	return floor(number + 0.5);
}

bool isBitSet(int number, int index){
    int powi = int(round(pow(2.0, float(index))));
	int ndp = number / powi;
    float mod_remainder = mod(float(ndp), 2.0);
	return !compareFloat(mod_remainder, 0.0);
}

int numberOfOnes(int number, int index){
	int numOnes = 0;
	int tmp = 128;
	for(int i = 7; i >= 0; i--){
		if(number >= tmp){
			number = number - tmp;
			if(i <= index){
				numOnes++;
			}
		}
		tmp = tmp / 2;
	}
	return numOnes;
}

float getLocalTreeDepth(vec3 positionOctree){
    int iOffset = 0;
	float depth = 0.0;
    vec3 offset = vec3(0.0, 0.0, 0.0);

	for(float i = 0.0; i <= 30.0; i++) {
		float nodeSizeAtLevel = octree_size  / pow(2.0, i);

        // Find the child index that we would belong to. We will test if it
        // actually exists w/ isBitSet(childrenNodes, index).
        vec3 index3d = (positionOctree - offset) / nodeSizeAtLevel;
		index3d = floor(index3d + 0.5);
        int index = int(round(index3d.x + 2.0 * index3d.y + 4.0 * index3d.z));

        // Grab our values
		vec4 value = texture2D(visible_nodes, vec2(float(iOffset) / 2048.0, 0.0));

        // Offet from our current node to its first child node
        int firstChildOffset = int(round(value.g * 255.0));

        // Bit representation of existing children
        // ex: child nodes: [0,1,4,7], childrenNodes = [10010011]
        int childrenNodes = int(round(value.r * 255.0));

        // Offset from the child node to the one we belong to
        // ex: child nodes: [0,1,4,7], index = 7 --> offsetToChildIndex = 3
        int offsetToChildIndex = numberOfOnes(childrenNodes, index - 1);

        // True if our suspected child exists
        bool childExists = isBitSet(childrenNodes, index);

		if (childExists) {
			// there are more visible child nodes at this position
			iOffset = iOffset + firstChildOffset + offsetToChildIndex;
            depth++;
		} else {
			// no more visible child nodes at this position
			return depth;
		}
		offset = offset + (vec3(1.0, 1.0, 1.0) * nodeSizeAtLevel * 0.5) * index3d;
	}

	return depth;
}

float getPointSizeAttenuation(vec3 pos_world){
    vec3 positionOctree = pos_world - octree_corner;
	return pow(1.9, getLocalTreeDepth(positionOctree));
}

void main() {
    vUv = uv;
    vAlpha = 1.0;
    vGPUColor = makeColor(gpu_index);

    vec4 mvPosition  = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;

    // Position in viewer coordinates (including offset)
	vec3 pos_world = (modelMatrix  * vec4( position, 1.0 )).xyz;
    float x = pos_world.x - origin.x;
    float y = pos_world.y - origin.y;
    float distance2D = sqrt(x * x + y * y);

    // Hide based on distance
    if (distance2D > distance) {
        vAlpha = 0.0;
    }

    // Check if classification is hidden
    if (classificationIsHidden(classification)) {
       vAlpha = 0.0;
    }

    // ---------------------
	// POINT SIZE
	// ---------------------
    float pointSize = 1.0;
    float projFactor = getProjFactor(fov, screenHeight, vViewPosition.z);

    if (dynamic_size && dynamic_cloud){
        // Dynamic sizing (based on node density)
    	float r = octree_spacing * 1.5;
        float pointAttenuation = getPointSizeAttenuation(pos_world);
    	float worldSpaceSize = 0.7 * size * r / pointAttenuation;
    	pointSize = 0.50 * worldSpaceSize * projFactor;
    } else {
        // Static point size
        pointSize = 0.05 * size * projFactor;
    }

    pointSize = max(size_min, pointSize);
    pointSize = min(size_max, pointSize);

    // ---------------------
	// POINT COLOR
	// ---------------------
    vColor = getCloudColor(color_type, pos_world);

    // ---------------------
    // CROSS SECTION
    // ---------------------
    bool inCrossSection = false; //checkForCrossSection(pos_world.xyz);

    // ---------------------
    // POINT IN POLYGON (MEASUREMENT AREA/VOLUME)
    // ---------------------
    int returnValue = checkPointInTriangle(pos_world.xyz);
    bool insidePolygon = returnValue > 0;
    bool volumeEnabled = returnValue == 2;

    if (inCrossSection || insidePolygon) {
        vColor = calculateOppositeColor(color_type, vColor);
    }

    if (volumeEnabled) {
        pointSize = pointSize * 0.5;
        // vAlpha = 0.0;
    }

    if (gpuPicker) {
        // gpu picker points are 10% larger than normal
        pointSize = pointSize * 1.10;
    }

    // Set OpenGL parameters
    gl_PointSize = pointSize;
}
`;

export default shader;